import { DateRange } from "react-day-picker";

import { useQuery } from "@tanstack/react-query";

import { supabase } from "~/integrations/supabase/client";

export const usePaymentStats = (dateRange?: DateRange, payments?: any[]) => {
  const hasDateFilter = !!dateRange?.from;

  // Query to fetch payment statistics
  const { data: stats } = useQuery({
    queryKey: [
      "payments-stats",
      dateRange?.from?.toISOString(),
      dateRange?.to?.toISOString(),
    ],
    queryFn: async () => {
      try {
        if (hasDateFilter) {
          // Use the same database function as Excel export for consistency
          let dateFrom = null;
          let dateTo = null;
          let recordLimit = 10000;

          if (dateRange?.from) {
            const fromDate = new Date(dateRange.from);
            fromDate.setHours(0, 0, 0, 0);
            dateFrom = fromDate.toISOString().split("T")[0]; // YYYY-MM-DD format

            if (dateRange.to) {
              // Fix timezone issue: extract date before timezone conversion
              const year = dateRange.to.getFullYear();
              const month = String(dateRange.to.getMonth() + 1).padStart(
                2,
                "0"
              );
              const day = String(dateRange.to.getDate()).padStart(2, "0");
              dateTo = `${year}-${month}-${day}`; // YYYY-MM-DD format without timezone conversion
            }
          }

          const { data, error } = await supabase.rpc(
            "get_payments_for_excel_export",
            {
              date_from: dateFrom,
              date_to: dateTo,
              record_limit: recordLimit,
            }
          );

          if (error) {
            console.error("Error fetching custom date range stats:", error);
            return {
              total: 0,
              week: 0,
              month: 0,
              count: 0,
            };
          }

          const paymentData = (data as any[]) || [];
          const filteredTotal = paymentData.reduce(
            (sum: number, payment: any) =>
              sum + (Number(payment.payment_amount) || 0),
            0
          );

          return {
            total: filteredTotal,
            week: 0, // Not showing in date range mode
            month: 0, // Not showing in date range mode
            count: paymentData.length,
          };
        } else {
          // Regular stats
          const { data: totalData, error: totalError } =
            await supabase.rpc("get_payments_total");

          const { data: weekData, error: weekError } = await supabase.rpc(
            "get_payments_this_week"
          );

          const { data: monthData, error: monthError } = await supabase.rpc(
            "get_payments_this_month"
          );

          if (totalError || weekError || monthError) {
            console.error(
              "Error fetching payment stats:",
              totalError || weekError || monthError
            );
            return {
              total: 0,
              week: 0,
              month: 0,
              count: 0,
            };
          }

          return {
            total: totalData || 0,
            week: weekData || 0,
            month: monthData || 0,
            count: payments?.length || 0,
          };
        }
      } catch (error) {
        console.error("Error in payment stats query:", error);
        return {
          total: 0,
          week: 0,
          month: 0,
          count: 0,
        };
      }
    },
    enabled: !!payments,
  });

  return { stats };
};
